import React, { useState, useEffect } from "react";
import { Box, Text, Flex, Spinner, Progress } from "@chakra-ui/react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading, forceAuthCheck } = useAuth();
  const location = useLocation();
  const [authCheckComplete, setAuthCheckComplete] = useState(false); // eslint-disable-line no-unused-vars
  const [authError, setAuthError] = useState<Error | null>(null); // eslint-disable-line no-unused-vars
  const [authCheckCount, setAuthCheckCount] = useState(0);
  const [isDelayedChecking, setIsDelayedChecking] = useState(false);
  const [delayProgress, setDelayProgress] = useState(0);

  // Log auth state on initial render and changes
  useEffect(() => {
    console.log("PROTECTED_ROUTE: Auth state changed:", {
      authenticated: !!currentUser,
      loading,
      userId: currentUser?.uid,
      email: currentUser?.email,
      isDelayedChecking,
      authCheckCount,
      path: location.pathname,
      timestamp: new Date().toISOString(),
    });
  }, [
    currentUser,
    loading,
    isDelayedChecking,
    authCheckCount,
    location.pathname,
  ]);

  // Handle delayed auth check if user is not found initially
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let progressInterval: NodeJS.Timeout | null = null;

    // Only start the delayed check if:
    // 1. Not already in a delayed check
    // 2. Not loading
    // 3. No current user
    if (!isDelayedChecking && !loading && !currentUser) {
      console.log("PROTECTED_ROUTE: Starting delayed authentication check", {
        time: new Date().toISOString(),
        loading,
        hasUser: !!currentUser,
        isDelayedChecking,
        authCheckCount,
      });

      setIsDelayedChecking(true);
      setDelayProgress(0);

      // Update progress bar every 50ms
      progressInterval = setInterval(() => {
        setDelayProgress((prev) => {
          const newValue = Math.min(prev + 1, 100);
          if (newValue === 25) {
            console.log("PROTECTED_ROUTE: Delayed auth check 25% complete");
          } else if (newValue === 50) {
            console.log("PROTECTED_ROUTE: Delayed auth check 50% complete");
          } else if (newValue === 75) {
            console.log("PROTECTED_ROUTE: Delayed auth check 75% complete");
          } else if (newValue === 100) {
            console.log(
              "PROTECTED_ROUTE: Progress reached 100%, waiting for timeout to complete"
            );
          }
          return newValue;
        });
      }, 50); // 5000ms (5s) / 100 steps = 50ms per step

      // After 5 seconds, force an auth check
      timeoutId = setTimeout(async () => {
        console.log(
          "PROTECTED_ROUTE: Delayed timeout completed, executing forceAuthCheck"
        );

        try {
          // Increment the check count for logging/tracking
          setAuthCheckCount((prevCount) => prevCount + 1);

          // Use the forceAuthCheck function from AuthContext
          const user = await forceAuthCheck();

          console.log("PROTECTED_ROUTE: Delayed auth check result:", {
            userFound: !!user,
            userId: user?.uid,
            email: user?.email,
            timestamp: new Date().toISOString(),
          });

          // Auth check is complete regardless of result
          setAuthCheckComplete(true);
        } catch (error) {
          console.error(
            "PROTECTED_ROUTE: Error during delayed auth check:",
            error
          );
          setAuthError(
            error instanceof Error ? error : new Error(String(error))
          );
        } finally {
          setIsDelayedChecking(false);
        }
      }, 5000);
    }

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        console.log(
          "PROTECTED_ROUTE: Cleanup - cleared delayed auth check timeout"
        );
      }
      if (progressInterval) {
        clearInterval(progressInterval);
        console.log("PROTECTED_ROUTE: Cleanup - cleared progress interval");
      }
    };
  }, [currentUser, loading, isDelayedChecking, authCheckCount, forceAuthCheck]);

  // If authenticated, render children
  if (currentUser) {
    return <>{children}</>;
  }

  // If we're either explicitly loading or doing a delayed check
  if (loading || isDelayedChecking) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        height="100vh"
        width="100%"
        p={5}
      >
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
          mb={4}
        />

        {isDelayedChecking && (
          <>
            <Progress
              value={delayProgress}
              colorScheme="blue"
              width="300px"
              mb={4}
              borderRadius="md"
            />
            <Text fontSize="md" textAlign="center" maxW="400px">
              Attempting to restore your session...
              {authCheckCount > 0 && " (Retry attempt #" + authCheckCount + ")"}
            </Text>
          </>
        )}

        {loading && !isDelayedChecking && <Text fontSize="md">Loading...</Text>}
      </Flex>
    );
  }

  // Not authenticated and not loading
  return (
    <Box
      p={5}
      textAlign="center"
      height="100vh"
      display="flex"
      flexDirection="column"
      justifyContent="center"
    >
      <Text fontSize="2xl" mb={2}>
        You need to sign in to access this page
      </Text>
      <Text fontSize="md" color="gray.600">
        Please sign in from the home page.
      </Text>
    </Box>
  );
};
